'use client';

/**
 * Host cutover factory for Core Analytics migrations.
 *
 * Apps ship migration work in small MRs (one Jira ticket per MR). While Core Analytics
 * lands incrementally on main, production must keep legacy instrumentation until cutover
 * completes. This module lets a host mount exactly one analytics path at a time—legacy or
 * core—so partial migrations do not drop production analytics coverage.
 */

import { createContext, useContext, type ReactNode } from 'react';

type AnalyticsHostProviderProps = {
  children: ReactNode;
};

export type AnalyticsHostOptions<TClient> = {
  /** The app's APP_NAME constant (e.g. `dx-offers2-ui`). Used in DevTools labels and errors. */
  appName: string;
  /** Runs only on the Core path, so it may read context mounted above `CoreAnalyticsHostProvider`. */
  useClient: () => TClient;
};

export type AnalyticsHost<TClient> = {
  LegacyAnalyticsHostProvider: (props: AnalyticsHostProviderProps) => ReactNode;
  CoreAnalyticsHostProvider: (props: AnalyticsHostProviderProps) => ReactNode;
  /** null on the legacy path; resolved client on the core path. */
  useAnalyticsHost: () => TClient | null;
};

/** Call once per app at module scope, then mount exactly one of the returned providers. */
export function createAnalyticsHost<TClient>({
  appName,
  useClient,
}: AnalyticsHostOptions<TClient>): AnalyticsHost<TClient> {
  const AnalyticsHostContext = createContext<TClient | null | undefined>(undefined);
  AnalyticsHostContext.displayName = `${appName}.AnalyticsHostContext`;

  function LegacyAnalyticsHostProvider({ children }: AnalyticsHostProviderProps) {
    return <AnalyticsHostContext.Provider value={null}>{children}</AnalyticsHostContext.Provider>;
  }
  LegacyAnalyticsHostProvider.displayName = `${appName}.LegacyAnalyticsHostProvider`;

  function CoreAnalyticsHostProvider({ children }: AnalyticsHostProviderProps) {
    const analytics = useClient();

    return (
      <AnalyticsHostContext.Provider value={analytics}>{children}</AnalyticsHostContext.Provider>
    );
  }
  CoreAnalyticsHostProvider.displayName = `${appName}.CoreAnalyticsHostProvider`;

  function useAnalyticsHost(): TClient | null {
    const analytics = useContext(AnalyticsHostContext);

    if (analytics === undefined) {
      throw new Error(`useAnalyticsHost must be used within an ${appName} analytics host provider`);
    }

    return analytics;
  }

  return { LegacyAnalyticsHostProvider, CoreAnalyticsHostProvider, useAnalyticsHost };
}
